import React, { useState, useEffect, useRef, useMemo } from "react";
import { Input, Form, Button } from "antd";
import "../ev-home-page.css";
import bannerBG from "../../../assets/img/bannerBG.png";
import Ripples from "react-ripples";
import _ from "lodash";
import { isEmpty } from "lodash";
import { useActions } from "../../../core-units/hooks/useActions";
import { useSelector } from "react-redux";

const Locations = (props) => {
  const { auth, servicesList, distance } = useActions();
  const authState = useSelector((state) => state.auth);
  const { routingValidation } = props;
  const homeLocation = "Ste 975/585 Little Collins St, Melbourne VIC 3000, Australia";
  const [selectedEvValuelocation, setSelectedEvValuelocation] = useState("");
  const [locSearchEvData, setLocSearchEvData] = useState([]);
  const [evFetching, setEvFetching] = useState(false);
  const [evLocDiff, setEvLocDiff] = useState(0);
  const loginAttempted = useRef(false); // log in only once per page load

  // NEW: autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const sessionTokenRef = useRef(null);
  const latestRequestRef = useRef(0);

  const parseLocationData = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch (error) {
      // ignore invalid JSON
    }
    if (typeof value === "string" && value.includes(",")) {
      return value.split(",").map((item) => Number(item.trim()));
    }
    return [];
  };

  const handleLogin = () => {
    if (loginInProgress || loginAttempted.current) {
      return;
    }
    loginAttempted.current = true; // one automatic attempt per page load

    auth({}, () => {
      servicesList();
    });
  };

  const authLoggedInValue = _.get(authState, "signupDetails.isLoggedIn", false);
  const loginInProgress = _.get(authState, "inProgress", false);
  const storedLoginStatus = localStorage.getItem("safebuyLoggedInStatus");
  const isLoggedIn =
    authLoggedInValue === true ||
    authLoggedInValue === "true" ||
    storedLoginStatus === "true";

  // ------------------ INITIAL LOAD ------------------
  useEffect(() => {
    setEvLocDiff(localStorage.getItem("evDiffLoc"));
    setLocSearchEvData(
      parseLocationData(
        localStorage.getItem("locSearchEvData") ||
          localStorage.getItem("locationSearchEvData"),
      ),
    );
    setSelectedEvValuelocation(
      localStorage.getItem("selectedEvValuelocation") || "",
    );
  }, []);

  // Auth check: runs once, no retry loop
  useEffect(() => {
    if (!isLoggedIn && !loginInProgress) {
      handleLogin();
    }
  }, [isLoggedIn, loginInProgress]);

  // ------------------ PLACES API (NEW) AUTOCOMPLETE ------------------

  // Loads the Places library (works with the classic <script ...libraries=places> tag)
  const loadPlacesLibrary = async () => {
    if (window.google?.maps?.importLibrary) {
      return window.google.maps.importLibrary("places");
    }
    return window.google.maps.places;
  };

  // Debounced fetch of suggestions
  const fetchSuggestions = useMemo(
    () =>
      _.debounce(async (input) => {
        const requestId = ++latestRequestRef.current;

        if (!input || input.trim().length < 2) {
          setSuggestions([]);
          setSuggestionsLoading(false);
          return;
        }

        try {
          setSuggestionsLoading(true);
          const { AutocompleteSessionToken, AutocompleteSuggestion } =
            await loadPlacesLibrary();

          if (!sessionTokenRef.current) {
            sessionTokenRef.current = new AutocompleteSessionToken();
          }

          const { suggestions: results } =
            await AutocompleteSuggestion.fetchAutocompleteSuggestions({
              input,
              includedRegionCodes: ["au"], // ISO 3166-1 alpha-2 (was "aus")
              sessionToken: sessionTokenRef.current,
            });

          // Ignore stale responses
          if (requestId !== latestRequestRef.current) return;

          setSuggestions(
            results.map((s) => s.placePrediction).filter(Boolean)
          );
        } catch (error) {
          console.error("[EV-Location] Autocomplete error", error);
          if (requestId === latestRequestRef.current) setSuggestions([]);
        } finally {
          if (requestId === latestRequestRef.current) {
            setSuggestionsLoading(false);
          }
        }
      }, 300),
    []
  );

  // Cancel any pending debounced call on unmount
  useEffect(() => {
    return () => fetchSuggestions.cancel();
  }, [fetchSuggestions]);

  // ------------------ LOCATION CHANGE ------------------
  const handleChange = (event) => {
    const address = event.target.value;
    setSelectedEvValuelocation(address);
    fetchSuggestions(address);
  };

  const handleSelect = async (prediction) => {
    const address = prediction.text.text;

    // Show the chosen address immediately and close the dropdown
    fetchSuggestions.cancel();
    latestRequestRef.current += 1;
    setSelectedEvValuelocation(address);
    setSuggestions([]);

    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["location", "formattedAddress"] });
      locationDifference({
        lat: place.location.lat(),
        lng: place.location.lng(),
      });
    } catch (error) {
      console.error("Error", error);
    } finally {
      // A selection ends the billing session; start a new one next time
      sessionTokenRef.current = null;
    }
  };

  const locationDifference = (latLng) => {
    const latLngD = [latLng.lat, latLng.lng];
    setLocSearchEvData(latLngD);
  };

  // ------------------ SUBMIT ------------------
  const onSubmit = () => {
    if (
      !isEmpty(selectedEvValuelocation) ||
      !_.isEmpty(localStorage.getItem("selectedEvValuelocation"))
    ) {
      localStorage.removeItem("evValue");
      localStorage.removeItem("evService");
      localStorage.removeItem("evTotal");
      localStorage.removeItem("evProduct");
      servicesList();

      document.getElementById("sample")?.scrollIntoView({
        behavior: "smooth",
      });

      const origin = homeLocation;
      const destination = selectedEvValuelocation;

      distance(
        {
          origin,
          destination,
          destinationAddress: selectedEvValuelocation,
          mode: "driving",
        },
        (value) => {
          console.log("Distance API response (EV):", value);
          const routes = _.get(value, "routes", []);
          let shortestDistanceKm = 0;

          if (routes.length > 0) {
            let shortestDistanceMeters = Number.MAX_SAFE_INTEGER;

            routes.forEach((route) => {
              (route.legs || []).forEach((leg) => {
                const distanceMeters = _.get(leg, "distanceMeters", _.get(leg, "distance.value", 0));
                if (distanceMeters < shortestDistanceMeters) {
                  shortestDistanceMeters = distanceMeters;
                }
              });
            });

            shortestDistanceKm = shortestDistanceMeters / 1000;
          }

          console.log("Shortest distance selected (EV):", shortestDistanceKm);
          setEvLocDiff(shortestDistanceKm);
          setEvFetching(false);
          localStorage.setItem("evDiffLoc", shortestDistanceKm);
        }
      );

      localStorage.setItem("selectedEvValuelocation", selectedEvValuelocation);
      localStorage.setItem("locSearchEvData", JSON.stringify(locSearchEvData));
    }
  };

  // ------------------ EDIT ------------------
  const onEdit = () => {
    [
      "evValue",
      "evService",
      "evTotal",
      "evProduct",
      "selectedEvValuelocation",
      "locSearchEvData",
      "locationSearchEvData",
      "evDiffLoc",
    ].forEach((key) => localStorage.removeItem(key));

    setSelectedEvValuelocation("");
    setLocSearchEvData([]);
    setEvLocDiff(0);
    setSuggestions([]);
    if (routingValidation.edit === 0) {
      setEvFetching(true);
    } else {
      routingValidation.home();
    }
  };

  // ------------------ UI ------------------
  const isInputEditable =
    routingValidation.route === false &&
    _.isEmpty(localStorage.getItem("selectedEvValuelocation"));

  return (
    <div
      className="EvBannerSection"
      style={{ backgroundImage: `url(${bannerBG})` }}
    >
      <div className="EvBannerTextContainer">
        <div className="container">
          <div className="row">
            <div className="col-12 col-md-4">
              <div className="EvBannerTextTitle">
                <span className="text-nowrap">Whenever you buy</span>
                <span className="txt-brown">SafeBuy</span>
              </div>

              <div className="EvBannerTextDesc">
                <b>
                  Book your inspection by entering the car location (
                  <span className="Evaddress">Complete address</span>)
                </b>
              </div>

              <div className="EvBannerInputContainer">
                <Form>
                  <Form.Item>
                    <div>
                      <Input
                        value={selectedEvValuelocation || ""}
                        onChange={handleChange}
                        placeholder="585 Collins Street, Melbourne, VIC 3000"
                        className="location-search-input"
                        autoComplete="off"
                        disabled={
                          routingValidation.route ||
                          !_.isEmpty(
                            localStorage.getItem("selectedEvValuelocation"),
                          )
                        }
                      />

                      {_.isEmpty(selectedEvValuelocation) && (
                        <span className="Evlocation-error-message">
                          Please fill this field!
                        </span>
                      )}

                      <div className="autocomplete-dropdown-container">
                        {suggestionsLoading && <div>Loading...</div>}
                        {suggestions.map((prediction) => (
                          <div
                            key={prediction.placeId}
                            role="option"
                            className="suggestion-item"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleSelect(prediction)}
                          >
                            {prediction.text.text}
                          </div>
                        ))}
                      </div>
                    </div>
                  </Form.Item>

                  <Form.Item>
                    <Ripples className="buttonRipple">
                      <Button
                        id="sample"
                        onClick={isInputEditable ? onSubmit : onEdit}
                      >
                        {isInputEditable ? "Get Started" : "Edit / Change"}
                      </Button>
                    </Ripples>
                  </Form.Item>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Locations;
